import type { Route } from 'next';
import type { ReactNode } from 'react';

import type { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { getIsMediaOffline } from '#cutils/media/getIsMediaOffline';
import { buildMediaStructuredData } from '#cutils/seo/schema-org/buildModuleMediaStructuredData';
import { resize } from '#utils/image/resize';

import type { MediaCollection } from './ArdPlayer/ArdPlayerInternal';
import type { MediaPlayerMedia, MediaPlayerPosterImage } from './MediaPlayer';
import { MediaPlayer } from './MediaPlayer';

type Props = {
	/**
	 * The copyright information for the media content.
	 */
	copyright?: string | null;
	/**
	 * The duration of the media content.
	 */
	duration: number | undefined;
	/**
	 * Additional meta data for the media content.
	 */
	meta?: string | null | undefined;
	/**
	 * The publication date of the media content.
	 */
	publicationDate?: string | null;
	/**
	 * The alt text for the thumbnail image.
	 */
	thumbnailAltText?: string | null;
	/**
	 * The copyright information for the thumbnail image.
	 */
	thumbnailCopyright?: string | null;
	/**
	 * The link for the thumbnail image.
	 */
	thumbnailLink?: Route | null;
	/**
	 * The URL of the thumbnail image.
	 */
	thumbnailUrl?: string | null;
	/**
	 * The title of the media content.
	 */
	title?: string | null;
	/**
	 * The URL of the media content.
	 */
	url: string;
	/**
	 * The mimeType of the url
	 *
	 * default: `application/vnd.apple.mpegurl`
	 */
	mimeType?: 'video/mp4' | 'application/vnd.apple.mpegurl';
	/**
	 * Indicates whether the media content is a priority.
	 */
	isPriority?: boolean;
	/**
	 * Indicates whether the media content is a hero.
	 */
	isHero?: boolean;
	/**
	 * Callback function to handle changes in the play status.
	 */
	onPlayStatusChange?(status: MediaContextPlaybackStatus): void;
	/**
	 * The ID of the played content, is needed for the analytics team.
	 */
	moduleId: number;
	/**
	 * set to `true` to enable the image zooming effect.
	 */
	enableZoomEffect?: boolean;
	sizes: string;
	playButtonTitle?: string;
	posterImageTitle?: ReactNode;
};

/**
 * Renders a media player component that can display video content.
 */
export function MediaVideo({
	copyright,
	duration,
	meta = '',
	publicationDate,
	thumbnailAltText,
	thumbnailCopyright,
	thumbnailLink,
	thumbnailUrl,
	title,
	url,
	mimeType = 'application/vnd.apple.mpegurl',
	posterImageTitle,
	isPriority,
	isHero,
	onPlayStatusChange,
	enableZoomEffect = false,
	sizes,
	playButtonTitle,
	moduleId: rowId,
}: Props) {
	let media: MediaPlayerMedia = { type: 'video' };

	const isMediaOnline = !getIsMediaOffline(meta);

	if (isMediaOnline && url) {
		const mediaCollection: MediaCollection = {
			// the meta information will be displayed within the information panel of
			// the player
			meta: {
				images: [],
				title: '',
				synopsis: title ?? '',
				clipSourceName: copyright ?? undefined,
			},
			streams: [
				{
					kind: 'main',
					media: [
						{
							// url: 'https://mcdn.br.de/br/fs/ard_alpha/hls/de/master.m3u8',
							url,
							mimeType,
							isAdaptiveQualitySelectable: true,
							audios: [
								{
									kind: 'standard',
									languageCode: 'de',
								},
							],
						},
					],
				},
			],
			pluginData: {
				'trackingPiano@web': {
					avContent: {
						av_content_id: `${rowId}`,
						av_content_type: 'Video',
						av_player_position: isHero ? 'Header' : 'Body',
					},
				},
			},
		};

		const structuredData = buildMediaStructuredData({
			duration,
			publicationDate,
			thumbnail: thumbnailUrl ? resize(thumbnailUrl, 'landscape', 'xl') : undefined,
			title,
			type: 'video',
			url,
		});

		media = {
			mediaCollection,
			structuredData,
			type: 'video',
		};
	}

	const posterImage: MediaPlayerPosterImage = {
		sizes,
		altText: thumbnailAltText,
		copyright: thumbnailCopyright,
		enableZoomEffect,
		imageUrl: thumbnailUrl,
		link: thumbnailLink,
		isPriority,
		playButtonTitle,
		isHero,
		title: posterImageTitle,
		showGradient: !!posterImageTitle,
	};

	return <MediaPlayer posterImage={posterImage} media={media} onPlayStatusChange={onPlayStatusChange} rowId={rowId} />;
}
